import { Subject, Observable } from 'rxjs';
import { Injectable, TemplateRef, InjectionToken, Injector, ViewContainerRef, ElementRef } from '@angular/core';
import { PortalInjector, ComponentPortal, ComponentType } from '@angular/cdk/portal';
import { Overlay, OverlayConfig, OverlayRef, ConnectedPosition } from '@angular/cdk/overlay';

export const PORTAL_DATA = new InjectionToken<any>('PortalData');

export interface ToastDefinition {
	icon?: string;
	message: string;
}

export interface ModalDefinition {
	icon: string;
	title: string;
	allowClose?: () => boolean;
	component: {
		name: string;
		theme?: string;
		class?: 'mediaclip-selector';
		inputs?: any;
		templateRef?: TemplateRef<any> | string;
	};
}

export interface NoticeDefinition {
	icon: string;
	title: string;
	type?: string;
	showCopy?: boolean;
	contents: string;
	onConfirm?: () => Observable<any> | void;
	onCopy?: () => void;
}

export interface DialogDefinition {
	icon: string;
	title: string;
	contents?: string;
	showSave?: boolean;
	templateRef?: TemplateRef<any>;
	onConfirm: () => Observable<any> | void;
	onCancel?: () => void;
	onSaveAndClose?: () => Observable<any> | void;
}

export interface OverlayOptions {
	registerBackdropClickHandler: boolean;
}

export interface Callback {
	function: () => any;
	this?: any;
	parameters?: any[];
}

@Injectable()
export class OverlayService {
	loadIds: string[] = [];

	// this service will coordinate all the items which can be placed on an overlay (priorities, positioning, ...)
	// so dialogs, modals, toasts, etc

	public createToastObservable: Subject<ToastDefinition>;
	public createModalObservable: Subject<ModalDefinition>;
	public removeModalObservable: Subject<ModalDefinition>;
	public createDialogObservable: Subject<DialogDefinition>;
	public createNoticeObservable: Subject<NoticeDefinition>;
	public loadingObservable: Subject<boolean>;

	constructor(private injector: Injector, public overlay: Overlay) {
		this.createToastObservable = new Subject<ToastDefinition>();
		this.createModalObservable = new Subject<ModalDefinition>();
		this.removeModalObservable = new Subject<ModalDefinition>();
		this.createDialogObservable = new Subject<DialogDefinition>();
		this.createNoticeObservable = new Subject<NoticeDefinition>();
		this.loadingObservable = new Subject<boolean>();
	}

	createToast(message: string, icon?: string) {
		const toast = { message, icon } as ToastDefinition;
		this.createToastObservable.next(toast);
	}

	createModal(modal: ModalDefinition) {
		this.createModalObservable.next(modal);
	}

	removeModal(modal: ModalDefinition) {
		this.removeModalObservable.next(modal);
	}

	createDialog(dialog: DialogDefinition) {
		this.createDialogObservable.next(dialog);
	}

	createNotice(notice: NoticeDefinition) {
		this.createNoticeObservable.next(notice);
	}

	setLoading(loading: boolean = true) {
		this.loadingObservable.next(loading);
	}

	increaseLoadCount(id: string) {
		if (this.loadIds.indexOf(id) === -1) {
			this.loadIds.push(id);
		}
		// console.warn(this.loadIds.length, 'increaseLoadCount', id);
		this.loadingObservable.next(true);
	}

	decreaseLoadCount(id: string) {
		const idIndex = this.loadIds.indexOf(id);
		if (idIndex !== -1) {
			this.loadIds.splice(idIndex, 1);
			// console.warn(this.loadIds.length, 'decreaseLoadCount  ---' , id);
		} else {
			// console.warn('decreaseLoadCount  didnt find ' , id);
		}
		if (this.loadIds.length === 0) {
			this.loadingObservable.next(false);
		}
	}

	public createConnectedOverlayComponent(
		component: ComponentType<any>, elementRef: ElementRef, viewContainerRef: ViewContainerRef, parameters: any,
		closeCallbacks?: Callback[], withPositions?: ConnectedPosition[], displayBackdrop = true
	) {

		// note: if displayBackdrop is FALSE, the backdrop click event is also not fired!
		const config = new OverlayConfig({
			hasBackdrop: displayBackdrop
		});

		if (!withPositions) {
			withPositions = [
				{
					originX: 'start', originY: 'bottom',
					overlayX: 'start', overlayY: 'top'
				}
			];
		}

		config.positionStrategy = this.overlay
			.position()
			.flexibleConnectedTo(elementRef)
			.withPositions(withPositions);

		const overlayRef: OverlayRef = this.overlay.create(config);

		overlayRef.backdropClick().subscribe((event: MouseEvent) => {
			event.stopPropagation();

			if (closeCallbacks) {
				closeCallbacks.forEach((callback: Callback) => {
					callback.function.apply(callback.this || null, callback.parameters || null);
				});
			}

			this.closePanel(overlayRef);
		});

		overlayRef.attach(new ComponentPortal(component, viewContainerRef,
			this.createInjector(parameters))
		);

		return overlayRef;
	}

	closePanel(overlayRef: OverlayRef) {
		if (overlayRef) {
			overlayRef.dispose();
			overlayRef = null;
		}
	}

	private createInjector(data: any): PortalInjector {

		const injectorTokens = new WeakMap<any, any>([
			[PORTAL_DATA, data],
		]);

		return new PortalInjector(this.injector, injectorTokens);
	}
}
